import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useEditor, useNode } from '@craftjs/core';

import Image from '@common/components/Image/Image';
import SettingsWrapper from '@common/components/SettingsWrapper/SettingsWrapper';
import { EDITOR_RESOLVERS, IMAGE_LIST_CONFIG } from '@common/constants';
import SettingsRow from '@common/components/SettingsWrapper/SettingsRow';
import SizeStyle from '@resolvers/tools/SizeStyle/SizeStyle';

import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { FormControl, InputLabel } from '@mui/material';
import TextWithPlaceholderInput from '@resolvers/tools/TextWithPlaceholderInput/TextWithPlaceholderInput';

const CraftImage = ({ src }) => {
  const { connectors } = useEditor();

  return (
    <Image
      ref={(ref) => connectors.create(ref, <CanvasImage src={src} />)}
      src={src}
      height={`${IMAGE_LIST_CONFIG.rowHeight}px`}
      alt={'no-image'}
      loading='lazy'
    />
  );
};

CraftImage.propTypes = {
  src: PropTypes.string,
};

const fitOptions = [
  { key: 'fill ', label: 'fill ', value: 'fill ' },
  { key: 'contain', label: 'contain', value: 'contain' },
  { key: 'cover', label: 'cover', value: 'cover' },
  { key: 'none', label: 'none', value: 'none' },
  { key: 'scale-down', label: 'scale-down', value: 'scale-down' },
];

export const CanvasImage = ({ src, width, height, objectFit, imageURL }) => {
  const { actions } = useEditor();
  const {
    connectors: { connect, drag },
    actions: { setCustom },
  } = useNode();

  const { t } = useTranslation();
  const ref = useRef(null);

  useEffect(() => {
    if (ref?.current && connect) {
      connect(drag(ref.current));
    }
  }, [connect]);

  useEffect(() => {
    actions.history.ignore(() => {
      setCustom((props) => {
        props.displayName = t('image');
      });
    });
  }, [setCustom, t]);

  const srcValue = imageURL ? imageURL : src.split('?')?.[0];

  return (
    <img
      ref={ref}
      src={srcValue || null}
      alt='Image'
      width={`${width}`}
      height={`${height}`}
      style={{
        maxWidth: `100%`,
        display: 'block',
        objectFit: `${objectFit}`,
        border: 0,
        outline: 'none',
        textDecoration: 'none',
      }}
    />
  );
};

function CanvasImageConfigPanel() {
  const {
    actions: { setProp },
    objectFit,
    imageURL,
  } = useNode(({ data }) => {
    return {
      objectFit: data?.props?.objectFit,
      imageURL: data?.props?.imageURL,
    };
  });

  const { t } = useTranslation();

  // eslint-disable-next-line no-param-reassign
  const handleSizeChange = (event) => {
    setProp((props) => {
      props.objectFit = event.target.value;
    }, 500);
  };

  const handleChangeButtonHref = (value) => {
    setProp((props) => {
      props.imageURL = value;
    }, 500);
  };

  return (
    <SettingsWrapper>
      <TextWithPlaceholderInput
        value={imageURL}
        setValue={handleChangeButtonHref}
        label={t('image_url')}
        placeholder={t('href_placeholder')}
        helperText={t('href_helper_text')}
        spaceDisabled={true}
      />
      <SettingsRow></SettingsRow>
      <SettingsRow>
        <SizeStyle
          label={t('width')}
          propKey='width'
          min={20}
          max={1000}
          disableHelperText={true}
        />
      </SettingsRow>
      <SettingsRow>
        <SizeStyle
          label={t('height')}
          propKey='height'
          min={20}
          max={1000}
          disableHelperText={true}
        />
      </SettingsRow>
      <SettingsRow>
        <FormControl
          fullWidth
          sx={{
            maxWidth: '223px',
          }}
        >
          <InputLabel size='small' id='demo-simple-select-label'>
            {t('fit')}
          </InputLabel>
          <Select
            labelId='demo-simple-select-label'
            id='demo-simple-select'
            value={objectFit}
            size='small'
            label={t('fit')}
            onChange={handleSizeChange}
          >
            {fitOptions.map((item) => (
              <MenuItem
                size='small'
                key={item.key}
                value={item.value}
                disabled={item?.disabled}
              >
                {t(item.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </SettingsRow>
    </SettingsWrapper>
  );
}

const CanvasImageDefaultProps = {
  width: '400',
  height: 'auto',
  objectFit: '',
  imageURL: '',
  src: '',
  isDeletable: true,
};

CanvasImage.defaultProps = CanvasImageDefaultProps;

CanvasImage.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string,
  imageURL: PropTypes.string,
  objectFit: PropTypes.string,
};

CanvasImage.craft = {
  displayName: EDITOR_RESOLVERS.COMMON.CANVAS_IMAGE,
  name: EDITOR_RESOLVERS.COMMON.CANVAS_IMAGE,
  props: CanvasImageDefaultProps,
  related: { settings: CanvasImageConfigPanel },
};

export default CraftImage;
